import {
  KeyMetricCardWithChart,
  KEY_METRIC_CARD_SHELL_CLASS,
} from "./KeyMetricCardWithChart";
import { useKeyMetricsRevenue } from "../../hooks/useKeyMetricsRevenue";
import { Skeleton } from "../shared/Skeleton";
export interface KeyMetricsRevenueSectionProps {
  /** Optional overrides for storybook/tests; when not provided, data comes from useKeyMetricsRevenue hook */
  loading?: boolean;
}

/** Skeleton matching compact KeyMetricCardWithChart / DsoKeyMetricCard shell */
function KeyMetricCardSkeleton({ withSubtext }: { withSubtext?: boolean }) {
  return (
    <div className={KEY_METRIC_CARD_SHELL_CLASS}>
      <Skeleton className="h-3 w-24 rounded" />
      <Skeleton className="h-10 w-36 rounded" />
      {withSubtext ? <Skeleton className="h-4 w-40 rounded" /> : null}
    </div>
  );
}

/**
 * Key Metrics section: FY revenue (closed months), collection rate (closed months), DSO for latest closed month.
 * Data via useKeyMetricsRevenue (Revenue page bootstrap when available).
 */
export function KeyMetricsRevenueSection({
  loading: loadingOverride,
}: KeyMetricsRevenueSectionProps = {}) {
  const {
    ytdRevenue,
    ytdSubtext,
    dsoAvgDays,
    dsoChangeText,
    dsoChangeVariant,
    dsoWindowLabel,
    loading: loadingFromHook,
    error,
  } = useKeyMetricsRevenue();

  const loading = loadingOverride ?? loadingFromHook;

  if (loading) {
    return (
      <div className="flex w-full flex-col items-start gap-4">
        <div className="flex items-center gap-2">
          <div className="flex h-4 w-0.5 flex-none flex-col items-start bg-neutral-900" />
          <span className="font-['Inter'] text-[10px] font-[500] leading-[12px] text-neutral-600 uppercase tracking-widest">
            Key Metrics
          </span>
        </div>
        <div className="flex w-full items-stretch gap-6 [&>*]:min-h-[150px]">
          <KeyMetricCardSkeleton withSubtext />
          <KeyMetricCardSkeleton />
          <KeyMetricCardSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex items-center gap-2">
        <div className="flex h-4 w-0.5 flex-none flex-col items-start bg-neutral-900" />
        <span className="font-['Inter'] text-[10px] font-[500] leading-[12px] text-neutral-600 uppercase tracking-widest">
          Key Metrics
        </span>
      </div>
      {error && <p className="text-body font-body text-error-600">{error}</p>}
      <div className="flex w-full items-stretch gap-6 [&>*]:min-h-[150px]">
        <KeyMetricCardWithChart
          title="FY Revenue (Closed)"
          value={ytdRevenue}
          subtext={ytdSubtext}
          valueClassName="text-neutral-900"
        />
        {/* <KeyMetricCardWithChart
          title="Collection Rate"
          value={collectionRateValue}
          subtext={ytdSubtext}
          valueClassName="text-neutral-900"
        /> */}
        <DsoKeyMetricCard
          avgDays={dsoAvgDays}
          changeText={dsoChangeText}
          changeVariant={dsoChangeVariant}
          windowLabel={dsoWindowLabel}
        />
      </div>
    </div>
  );
}

function DsoKeyMetricCard({
  avgDays,
  changeText,
  changeVariant,
  windowLabel,
}: {
  avgDays: number | null;
  changeText?: string;
  changeVariant?: "up" | "down";
  windowLabel?: string;
}) {
  const displayAvg = avgDays != null ? String(avgDays) : "—";
  const periodLabel = windowLabel?.trim() || "Latest closed month";

  return (
    <div className={KEY_METRIC_CARD_SHELL_CLASS}>
      <div className="flex flex-col gap-0.5">
        <span className="text-caption font-caption text-neutral-400 uppercase tracking-wider">
          DSO for {periodLabel}
        </span>
      </div>
      <span className="font-['Inter'] text-[48px] font-[700] leading-[48px] text-neutral-900">
        {displayAvg}
      </span>
      {changeText ? (
        <div className="flex items-center gap-2">
          <span
            className={`text-body font-body ${changeVariant === "up" ? "text-error-600" : "text-success-600"}`}
          >
            {changeText}
          </span>
          <span className="text-body font-body text-neutral-500">
            from last month
          </span>
        </div>
      ) : (
        <span className="text-body font-body text-neutral-500">
          from last month
        </span>
      )}
    </div>
  );
}
